import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import { PRICING } from '@/lib/stripe';
import type { UserProfile } from '@/lib/admin';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Crown, Check, Loader2, ExternalLink } from 'lucide-react';

interface SubscriptionCardProps {
  userProfile: UserProfile | null;
  isPremium: boolean;
}

export function SubscriptionCard({ userProfile, isPremium }: SubscriptionCardProps) {
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const createSubscription = httpsCallable(functions, 'createSubscription');
      const result = await createSubscription({
        priceId: PRICING.subscription.premium.priceId,
        successUrl: `${window.location.origin}/dashboard?subscription=success`,
        cancelUrl: `${window.location.origin}/dashboard`,
      });

      const data = result.data as { url: string };
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Failed to create subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const createPortalSession = httpsCallable(functions, 'createCustomerPortalSession');
      const result = await createPortalSession({});
      const data = result.data as { url: string };
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Failed to open subscription portal:', error);
    } finally {
      setPortalLoading(false);
    }
  };

  const subscriptionStatus = userProfile?.subscriptionStatus;
  const periodEnd = userProfile?.subscriptionCurrentPeriodEnd;
  const isGracePeriod = subscriptionStatus === 'canceled' && periodEnd && new Date(periodEnd) > new Date();

  if (isPremium) {
    return (
      <Card className="border-primary/30 bg-primary/5">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Premium Plan</CardTitle>
            <Badge variant="default" className="ml-auto">Active</Badge>
          </div>
          {isGracePeriod && periodEnd && (
            <CardDescription className="text-amber-600">
              Canceled — access until {new Date(periodEnd).toLocaleDateString()}
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className="pt-0">
          <ul className="space-y-1.5 text-sm mb-4">
            {PRICING.subscription.premium.features.map((feature) => (
              <li key={feature} className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
          {userProfile?.stripeCustomerId && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleManageSubscription}
              disabled={portalLoading}
            >
              {portalLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <ExternalLink className="h-4 w-4 mr-2" />
              )}
              Manage Subscription
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Crown className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-lg">Upgrade to Premium</CardTitle>
        </div>
        <CardDescription>
          ${PRICING.subscription.premium.price}/month
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        <ul className="space-y-1.5 text-sm mb-4">
          {PRICING.subscription.premium.features.map((feature) => (
            <li key={feature} className="flex items-center gap-2">
              <Check className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
        <Button
          onClick={handleSubscribe}
          disabled={loading}
          className="w-full"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Crown className="h-4 w-4 mr-2" />
          )}
          Subscribe Now
        </Button>
      </CardContent>
    </Card>
  );
}
